/**
 * React Router manifest for every CRM page.tsx in `app/(authenticated)/`.
 * The CRM source uses Next.js file-based routing, which we replace by
 * importing each page's default export and registering an explicit
 * <Route /> here. Bracket-segment paths (`[id]`) become `:id`.
 *
 * 47 pages total. None of the imported files are modified.
 */
import type { ReactElement } from "react";
import { Navigate, Route, useLocation, useParams } from "react-router-dom";

import {
  CrmLandingRedirect,
  RequireFinancialOutlet,
} from "@crm-src/components/crm-route-guards";

import {
  emailCampaignEditPath,
  emailCampaignLogsPath,
} from "@crm-src/lib/email-campaign-routes";

import AdminResellerDetailPage from "@crm-src/pages/administration/resellers/detail/AdministrationResellersDetailPage";
import AdminResellersPage from "@crm-src/pages/administration/resellers/AdministrationResellersPage";
import ResellerApplicationReviewPage from "@crm-src/pages/administration/resellers/applications/ResellerApplicationDetailPage";
import AdminSystemActivityPage from "@crm-src/pages/administration/system-activity/AdministrationSystemActivityPage";
// There is NO user / role / tenant management UI inside /crm. All
// system-account-and-privileges control lives at /admin — the CRM
// fully respects the dashboard's session and reads its dashboard
// role from the same JWT every other tab uses. Operational pages
// (product catalog, audit log, API connections,
// resellers) stay here because they are CRM business surfaces, not
// identity surfaces.
//
// The source files for the removed admin pages were deleted along
// with their dialog components and API clients (accounts-api,
// roles-api, super-administrator/*). Only redirects remain below
// for old bookmarks.
import ContactsHubPage from "@crm-src/pages/contacts/ContactsHubPage";
import CustomerDetailPage from "@crm-src/pages/customers/detail/CustomersDetailPage";
import CustomersPage from "@crm-src/pages/customers/CustomersPage";
import CustomersWorldMapPage from "@crm-src/pages/customers/world-map/CustomersWorldMapPage";
import FileManagerPage from "@crm-src/pages/file-manager/FileManagerPage";
import LicenceBuilderPage from "@crm-src/pages/licences/LicenceBuilderPage";
import LicencesPage from "@crm-src/pages/licences/LicencesPage";
import PricingPage from "@crm-src/pages/pricing/PricingPage";
import ReleaseDetailPage from "@crm-src/pages/releases/ReleaseDetailPage";
import ReleasesPage from "@crm-src/pages/releases/ReleasesPage";
import { legacyReleasesTarget } from "@crm-src/pages/releases/release-paths";
import FinancialMetricsCompanyPage from "@crm-src/pages/financial-metrics/company/FinancialMetricsCompanyPage";
import FinancialMetricsLayout from "@crm-src/pages/financial-metrics/FinancialMetricsLayout";
import FinancialMetricsPage from "@crm-src/pages/financial-metrics/FinancialMetricsPage";
import FinancialMetricsProductsPage from "@crm-src/pages/financial-metrics/products/FinancialMetricsProductsPage";
import FinancialMetricsSalespersonPage from "@crm-src/pages/financial-metrics/salespeople/detail/FinancialMetricsSalespeopleDetailPage";
import FinancialMetricsSalespeoplePage from "@crm-src/pages/financial-metrics/salespeople/FinancialMetricsSalespeoplePage";
import InvoiceDetailPage from "@crm-src/pages/invoices/detail/InvoicesDetailPage";
import InvoiceGeneratePage from "@crm-src/pages/invoices/generate/InvoicesGeneratePage";
import InvoicesPage from "@crm-src/pages/invoices/InvoicesPage";
import InvoicesRecurringPage from "@crm-src/pages/invoices/recurring/InvoicesRecurringPage";
import InvoiceScheduledDetailPage from "@crm-src/pages/invoices/scheduled/detail/InvoicesScheduledDetailPage";
import InvoiceScheduleDetailPage from "@crm-src/pages/invoices/schedules/detail/InvoicesSchedulesDetailPage";
import InvoicesSchedulesPage from "@crm-src/pages/invoices/schedules/InvoicesSchedulesPage";
import ProfilePage from "@crm-src/pages/profile/ProfilePage";
import AiProspectsPage from "@crm-src/pages/prospects/AiProspectsPage";
import ProspectDetailPage from "@crm-src/pages/prospects/detail/ProspectsDetailPage";
import ProspectsPage from "@crm-src/pages/prospects/ProspectsPage";
import PurchaseOrderDetailPage from "@crm-src/pages/purchase-orders/detail/PurchaseOrderDetailPage";
import PurchaseOrdersPage from "@crm-src/pages/purchase-orders/PurchaseOrdersPage";
import PreShipQuestionnairesPage from "@crm-src/pages/pre-ship/PreShipQuestionnairesPage";
import PreShipQuestionnaireDetailPage from "@crm-src/pages/pre-ship/PreShipQuestionnaireDetailPage";
import QuoteDetailPage from "@crm-src/pages/quotes/detail/QuotesDetailPage";
import QuoteGeneratePage from "@crm-src/pages/quotes/generate/QuotesGeneratePage";
import QuotesPage from "@crm-src/pages/quotes/QuotesPage";
import TasksPage from "@crm-src/pages/tasks/TasksPage";

/** `/crm/licences/releases?release=10.2` (the joint section's Releases tab,
 *  until 2026-09-13) → `/crm/releases/10.2`. The open folder becomes the path. */
function LegacyReleasesRedirect(): ReactElement {
  const { search } = useLocation();
  return <Navigate to={legacyReleasesTarget(search)} replace />;
}

/**
 * Preserves the campaign id when redirecting a legacy
 * `/crm/email-campaigns/edit/:id` bookmark to its new Marketing home.
 */
function RedirectToCampaignEdit(): ReactElement {
  const { id } = useParams();
  return <Navigate to={emailCampaignEditPath(id)} replace />;
}

/**
 * Same for a legacy `/crm/email-campaigns/:id/logs` bookmark. This one used
 * to <Navigate> to the bare campaigns list and DROP the id — the page it
 * pointed at was orphaned entirely (2026-06-02 → 2026-08-28), so nobody
 * noticed the deep-link was being thrown away.
 */
function RedirectToCampaignLogs(): ReactElement {
  const { id } = useParams();
  return <Navigate to={emailCampaignLogsPath(id)} replace />;
}

/**
 * The CRM <Route> tree. Used inside the parent `<Route path="crm" ...>`
 * in App.tsx so every URL here is relative to `/crm`.
 *
 * Returns an array of JSX elements (not a single fragment) so they can
 * be spread into the parent route element. React Router doesn't allow
 * Fragment as a Route child container.
 */
export const crmRouteElements = (
  <>
    {/* /crm lands on the FIRST page this user can access. With granular page
        permissions a role may not have Tasks (a marketing manager sees only
        Customers + Prospects), so a hard redirect to Tasks would dead-end. */}
    <Route index element={<CrmLandingRedirect />} />

    <Route path="profile" element={<ProfilePage />} />

    {/* Contacts & Groups — ONE page, two tabs (2026-09-12). Contact Groups
        used to be its own nav entry + URL; it is the `?tab=groups` tab now. */}
    <Route path="contacts" element={<ContactsHubPage />} />
    <Route path="contact-groups" element={<Navigate to="/crm/contacts?tab=groups" replace />} />

    {/* Regular vs AI prospects are two sibling pages (2026-06-05). `ai-prospects`
        is intentionally a sibling of `prospects` — NOT `prospects/ai` — so it can
        never shadow the `prospects/:id` detail route. Both share the prospect
        detail page below; a detail page's "Back" returns to the correct list via
        `prospectListPathForLead` (see prospects-origin.ts). */}
    <Route path="prospects" element={<ProspectsPage />} />
    <Route path="ai-prospects" element={<AiProspectsPage />} />
    <Route path="prospects/:id" element={<ProspectDetailPage />} />

    <Route path="customers" element={<CustomersPage />} />
    <Route path="customers/:id" element={<CustomerDetailPage />} />
    {/* Pre-ship questionnaires (2026-10-03) — customer data, guarded by the
        Customers page inside each page (withCustomersAccess). */}
    <Route path="pre-ship" element={<PreShipQuestionnairesPage />} />
    <Route path="pre-ship/:id" element={<PreShipQuestionnaireDetailPage />} />
    <Route path="customers/world-map" element={<CustomersWorldMapPage />} />

    <Route path="tasks" element={<TasksPage />} />

    {/* Financial wall (route half): Invoices + Quotes are money pages, so they
        sit behind a `financials:access` guard — a direct URL is Access-Denied,
        not just hidden from the nav. */}
    <Route element={<RequireFinancialOutlet />}>
      <Route path="invoices" element={<InvoicesPage />} />
      <Route path="invoices/generate" element={<InvoiceGeneratePage />} />
      <Route path="invoices/recurring" element={<InvoicesRecurringPage />} />
      <Route path="invoices/scheduled/:id" element={<InvoiceScheduledDetailPage />} />
      <Route path="invoices/schedules" element={<InvoicesSchedulesPage />} />
      <Route path="invoices/schedules/:id" element={<InvoiceScheduleDetailPage />} />
      <Route path="invoices/:id" element={<InvoiceDetailPage />} />

      <Route path="quotes" element={<QuotesPage />} />
      <Route path="quotes/generate" element={<QuoteGeneratePage />} />
      <Route path="quotes/:id" element={<QuoteDetailPage />} />

      {/* Purchase orders a customer or reseller sent XOP (2026-09-17) — money
          documents, so behind the same financial guard. */}
      <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
      <Route path="purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
    </Route>

    {/* Email Campaigns moved to the Marketing section (2026-06-02):
        /marketing/campaigns. The pages still live under src/crm and run on
        the CRM runtime — they're just surfaced under Marketing now and gone
        from the CRM nav. Keep redirects so old /crm bookmarks land on the
        new home (both deep-links — edit and Sendout Logs — keep their
        :id). */}
    <Route path="email-campaigns" element={<Navigate to="/marketing/campaigns" replace />} />
    <Route path="email-campaigns/create" element={<Navigate to="/marketing/campaigns/create" replace />} />
    <Route path="email-campaigns/edit/:id" element={<RedirectToCampaignEdit />} />
    <Route path="email-campaigns/:id/logs" element={<RedirectToCampaignLogs />} />

    <Route path="file-manager" element={<FileManagerPage />} />

    {/* Releases and Licences — TWO sidebar entries since 2026-09-13 (they
        shared one entry and a tab strip for a day). Licences are
        route-guarded on `licences:access` inside the pages; Releases keeps
        its own `products_pricing:access` grant. */}
    {/* Releases: the folders, and nothing else. A release is a PLACE —
        `/crm/releases/10.2` — with the versions inside it, each carrying its
        downloads, change log, documentation and download journal. */}
    <Route path="releases" element={<ReleasesPage />} />
    <Route path="releases/:release" element={<ReleaseDetailPage />} />
    {/* The joint section's Releases tab, with `?release=` for the open folder,
        lived at this address until 2026-09-13. The param becomes the path. */}
    <Route path="licences/releases" element={<LegacyReleasesRedirect />} />

    <Route path="licences" element={<LicencesPage />} />
    {/* The Access journal page was REMOVED on 2026-09-13 (CEO). The recording
        is untouched — crm-api still journals every touch, the builder still
        lists a licence's downloads — only the page folding the feed by
        person is gone, so its addresses land on the list. */}
    <Route path="licences/journal" element={<Navigate to="/crm/licences" replace />} />
    <Route path="licences/reports" element={<Navigate to="/crm/licences" replace />} />
    {/* `new` is the builder in create mode (?customer=&name=, or ?from= to
        duplicate an issued licence onto another machine). Nothing exists
        until Generate is pressed. */}
    <Route path="licences/new" element={<LicenceBuilderPage />} />
    <Route path="licences/:id" element={<LicenceBuilderPage />} />

    {/* Pricing — ONE page, no tabs, guarded on `products_pricing:access`
        inside it. The catalogue and the price book merged on 2026-09-13 (a
        feature carries its own price), so "Products & Pricing" became
        "Pricing" and `/crm/products-pricing` redirects here. Releases and
        their Download report left for `/crm/releases` a day earlier;
        `?tab=downloads` still redirects there. The former System Admin URLs
        (`/admin/products-releases`, `/admin/price-list`) redirect here from
        App.tsx. */}
    <Route path="pricing" element={<PricingPage />} />
    <Route path="products-pricing" element={<PricingPage />} />

    {/* Financial wall (route half): the revenue dashboards are the most
        money-dense pages — same `financials:access` guard as Invoices. */}
    <Route element={<RequireFinancialOutlet />}>
      <Route path="financial-metrics" element={<FinancialMetricsLayout><FinancialMetricsPage /></FinancialMetricsLayout>} />
      <Route path="financial-metrics/company" element={<FinancialMetricsLayout><FinancialMetricsCompanyPage /></FinancialMetricsLayout>} />
      <Route path="financial-metrics/products" element={<FinancialMetricsLayout><FinancialMetricsProductsPage /></FinancialMetricsLayout>} />
      <Route path="financial-metrics/salespeople" element={<FinancialMetricsLayout><FinancialMetricsSalespeoplePage /></FinancialMetricsLayout>} />
      <Route path="financial-metrics/salespeople/:id" element={<FinancialMetricsLayout><FinancialMetricsSalespersonPage /></FinancialMetricsLayout>} />
    </Route>

    {/* RETIRED 2026-09-14: API Connections. The support portal now reaches
        crm-api over a signed service token minted by portal-api itself, so
        there is no per-integration API key for an admin to create or rotate.
        The pages are deleted; old bookmarks land on the CRM smart landing. */}
    <Route path="administration/api-connections" element={<Navigate to="/crm" replace />} />
    <Route path="administration/api-connections/:id" element={<Navigate to="/crm" replace />} />
    {/* The old CRM product manager was a SECOND catalogue editor, unlinked
        since 2026-09-12 and still offering the version level that went away on
        2026-09-13. Deleted; the bookmark lands on the one page that manages
        the catalogue and its prices. */}
    <Route path="administration/product-manager" element={<Navigate to="/crm/pricing" replace />} />
    <Route path="administration/resellers" element={<AdminResellersPage />} />
    <Route path="administration/resellers/:id" element={<AdminResellerDetailPage />} />
    {/* One reseller APPLICATION under review (2026-09-12, moved from System
        Admin). The list is the Reseller Applications tab of the page above;
        the page itself guards on `reseller_applications:access`. */}
    <Route path="administration/reseller-applications/:id" element={<ResellerApplicationReviewPage />} />
    <Route path="administration/system-activity" element={<AdminSystemActivityPage />} />
    {/* DISCONTINUED 2026-09-12: Tenant Settings + Portal Access Queue (and
        the queue's pre-rename URL). The pages are deleted; old bookmarks
        land on the CRM smart landing instead of a 404. */}
    <Route path="administration/tenant-settings" element={<Navigate to="/crm" replace />} />
    <Route path="administration/portal-access-queue" element={<Navigate to="/crm" replace />} />
    <Route path="administration/support-portal-sync" element={<Navigate to="/crm" replace />} />

    {/* Identity surfaces (accounts, role definitions, tenants,
        cross-tenant users) live ONLY at /admin. Every URL that used
        to host one of those panels under /crm <Navigate>s there so
        bookmarks survive. */}
    <Route
      path="administration/manage-accounts"
      element={<Navigate to="/admin" replace />}
    />
    <Route
      path="administration/roles-privileges"
      element={<Navigate to="/admin" replace />}
    />
    <Route path="super-administrator" element={<Navigate to="/admin" replace />} />
    <Route
      path="super-administrator/tenants"
      element={<Navigate to="/admin" replace />}
    />
    <Route
      path="super-administrator/users"
      element={<Navigate to="/admin" replace />}
    />
  </>
);
